import * as React from 'react';
import { NavLink } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  FileText,
  Globe,
  Hammer,
  LayoutDashboard,
  Megaphone,
  Package,
  Percent,
  Settings,
  ShoppingCart,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@dfq/shared/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dfq/shared/components/ui/tooltip';
import { cn } from '@dfq/shared/lib/utils';

/**
 * Admin console sidebar — 240px fixed chrome with a 9-item primary nav,
 * per Figma `23:3` (Console file `6wsm2RHMiHYoYcnW7Y38kL`).
 *
 * Layout
 * - Expanded width: 240px. Collapsed (`collapsed` prop): 64px icon rail,
 *   labels hidden, shadcn `Tooltip` surfaces the label on hover.
 * - 64px brand header (logo + "DFQ Printing" wordmark in expanded mode).
 * - Section label ("MAIN MENU") in the Foundations `ui-section-label`
 *   scale, uppercase, opacity-50 muted per the canvas.
 * - Each row: 44px tall; 3px left active indicator in `bg-brand-primary`
 *   (accent/primary indigo); active row background `bg-sidebar-active`,
 *   active text `text-sidebar-active-foreground`.
 *
 * Sub-nav
 * Marketing, SEO, and Settings have sub-routes (Coupons/Promotions,
 * Redirects, and settings sub-pages respectively) that render inline via
 * shadcn `Accordion`. The component is stateless — the open group is
 * derived from `activePath`.
 *
 * RBAC/permissions are not part of this capsule; consuming layouts can
 * filter `NAV_ITEMS` / `SUB_NAV` before rendering if needed.
 */

export interface AdminSidebarNavItem {
  /** Machine id — also used as the Accordion item value when grouped. */
  id: string;
  /** Absolute route path this nav entry points at. */
  path: string;
  /** Display label. */
  label: string;
  /** Lucide icon component. */
  icon: LucideIcon;
  /** Optional collapsible children rendered inline when the parent matches. */
  children?: ReadonlyArray<Omit<AdminSidebarNavItem, 'children' | 'icon'> & { icon?: LucideIcon }>;
  /**
   * Count of items needing attention. Rendered as a chip on the row, and as a
   * dot when the sidebar is collapsed — a nav entry nobody looks at cannot do
   * its job. Zero and undefined both render nothing.
   */
  badgeCount?: number;
}

/**
 * Canonical admin nav — order is load-bearing; matches the 9-item list
 * spec'd in CON-DS-002 and the Figma frame.
 */
export const ADMIN_NAV_ITEMS: ReadonlyArray<AdminSidebarNavItem> = [
  {
    id: 'dashboard',
    path: '/admin',
    label: 'Dashboard',
    icon: LayoutDashboard,
  },
  { id: 'products', path: '/admin/products', label: 'Products', icon: Package },
  { id: 'orders', path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { id: 'customers', path: '/admin/customers', label: 'Customers', icon: Users },
  { id: 'prepress', path: '/admin/prepress', label: 'Prepress', icon: FileText },
  { id: 'production', path: '/admin/production', label: 'Production', icon: Hammer },
  { id: 'damage-claims', path: '/admin/damage-claims', label: 'Damage Claims', icon: AlertTriangle },
  {
    id: 'marketing',
    path: '/admin/coupons',
    label: 'Marketing',
    icon: Megaphone,
    children: [
      { id: 'coupons', path: '/admin/coupons', label: 'Coupons', icon: Ticket },
      { id: 'promotions', path: '/admin/promotions', label: 'Promotions', icon: Percent },
    ],
  },
  {
    id: 'seo',
    path: '/admin/seo/redirects',
    label: 'SEO',
    icon: Globe,
    children: [
      { id: 'seo-redirects', path: '/admin/seo/redirects', label: 'Redirects' },
    ],
  },
  { id: 'reports', path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  {
    id: 'settings',
    path: '/admin/settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { id: 'seo', path: '/admin/settings/seo', label: 'SEO' },
      { id: 'quotes', path: '/admin/settings/quotes', label: 'Quotes' },
    ],
  },
];

const sidebarVariants = cva(
  'flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
  {
    variants: {
      collapsed: {
        true: 'w-16',
        false: 'w-60',
      },
    },
    defaultVariants: { collapsed: false },
  },
);

export interface AdminSidebarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'>,
    VariantProps<typeof sidebarVariants> {
  /** Render as icon-only 64px rail with tooltips on hover. */
  collapsed?: boolean;
  /** Current active route path (e.g. `/admin/marketing/promotions`). */
  activePath: string;
  /**
   * Override the default {@link ADMIN_NAV_ITEMS}. Consuming layouts pass a
   * pre-filtered list when RBAC needs to hide sections the current user
   * lacks access to (CON-AUTH-001). When omitted, the full 9-section
   * canonical admin nav renders.
   */
  navItems?: ReadonlyArray<AdminSidebarNavItem>;
}

/**
 * A path "matches" a nav target when it is exactly the target path OR is
 * a descendant. `/admin` is exact-only (otherwise every subtree would
 * claim Dashboard as active).
 */
function isActivePath(activePath: string, targetPath: string): boolean {
  if (targetPath === '/admin') return activePath === '/admin';
  return activePath === targetPath || activePath.startsWith(`${targetPath}/`);
}

type NavRowItem =
  | AdminSidebarNavItem
  | Omit<AdminSidebarNavItem, 'children' | 'icon'>
  | (Omit<AdminSidebarNavItem, 'children' | 'icon'> & { icon?: LucideIcon });

function NavRow({
  item,
  active,
  collapsed,
  asSubItem = false,
}: {
  item: NavRowItem;
  active: boolean;
  collapsed: boolean;
  asSubItem?: boolean;
}) {
  const Icon = 'icon' in item ? item.icon : undefined;
  const badgeCount = 'badgeCount' in item ? (item.badgeCount ?? 0) : 0;
  const content = (
    <div
      className={cn(
        'flex h-11 w-full items-center overflow-hidden',
        // 3px indicator rail on the left — always rendered so the
        // text alignment stays stable between active / inactive rows.
        "before:block before:h-11 before:w-[3px] before:shrink-0 before:content-['']",
        active ? 'before:bg-brand-primary' : 'before:bg-transparent',
      )}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 py-3 pr-5 pl-4',
          asSubItem && !collapsed && 'pl-11',
        )}
      >
        {Icon ? (
          <Icon
            size={16}
            className={cn(
              'shrink-0',
              active ? 'text-sidebar-active-foreground' : 'text-sidebar-foreground',
            )}
            aria-hidden="true"
          />
        ) : null}
        {!collapsed ? (
          <span
            className={cn(
              'truncate text-ui-nav',
              active
                ? 'text-ui-nav-active text-sidebar-active-foreground'
                : 'text-sidebar-foreground',
            )}
          >
            {item.label}
          </span>
        ) : (
          <span className="sr-only">{item.label}</span>
        )}

        {badgeCount > 0 ? (
          <span
            data-testid={`admin-sidebar-badge-${item.id}`}
            aria-label={`${badgeCount} needing attention`}
            className={cn(
              'ml-auto shrink-0 rounded-full bg-amber-100 text-center font-medium text-amber-800',
              collapsed
                ? 'absolute top-2 right-2 size-2 p-0'
                : 'min-w-5 px-1.5 py-0.5 text-xs',
            )}
          >
            {collapsed ? '' : badgeCount > 99 ? '99+' : badgeCount}
          </span>
        ) : null}
      </div>
    </div>
  );

  const link = (
    <NavLink
      to={item.path}
      end={item.path === '/admin'}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : undefined}
      className={cn(
        'relative flex w-full items-stretch outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-inset',
        active ? 'bg-sidebar-active' : 'hover:bg-sidebar-active/60',
      )}
    >
      {content}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export function AdminSidebar({
  collapsed = false,
  activePath,
  navItems = ADMIN_NAV_ITEMS,
  className,
  ...rest
}: AdminSidebarProps) {
  // Which accordion groups should be open. Derived from `activePath` so
  // the component stays stateless — parent can rerender with a new path
  // and the correct group opens without any local state plumbing.
  //
  // A group opens when the active path matches the parent OR any of its
  // children — necessary when child routes don't share the parent's prefix
  // (e.g. Marketing with children at /admin/coupons and /admin/promotions).
  const openGroups = React.useMemo(
    () =>
      navItems
        .filter(
          (item) =>
            item.children &&
            (isActivePath(activePath, item.path) ||
              item.children.some((child) => isActivePath(activePath, child.path))),
        )
        .map((item) => item.id),
    [activePath, navItems],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <nav
        aria-label="Admin navigation"
        className={cn(sidebarVariants({ collapsed }), className)}
        {...rest}
      >
        {/* Logo / brand */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center gap-3 overflow-hidden border-b border-sidebar-border',
            collapsed ? 'justify-center px-2' : 'px-5',
          )}
          data-node-id="67:2"
        >
          <div
            aria-hidden="true"
            className="size-[34px] shrink-0 rounded-[9px] bg-brand-primary"
          />
          {!collapsed ? (
            <span className="truncate font-sans text-[16px] font-bold text-on-primary">
              DFQ Printing
            </span>
          ) : null}
        </div>

        {/* Section label — hidden in collapsed mode to keep the rail clean */}
        {!collapsed ? (
          <div className="flex h-11 items-center pt-4 pb-2 pl-5" data-node-id="67:6">
            <span className="text-ui-section-label tracking-[1px] text-sidebar-foreground opacity-50 uppercase">
              MAIN MENU
            </span>
          </div>
        ) : (
          <div className="h-4" aria-hidden="true" />
        )}

        {/* Primary nav */}
        <Accordion
          type="multiple"
          value={openGroups}
          // onValueChange intentionally omitted — state is path-derived.
          className="flex flex-col"
        >
          {navItems.map((item) => {
            const active = isActivePath(activePath, item.path);

            if (!item.children) {
              return (
                <NavRow
                  key={item.id}
                  item={item}
                  active={active}
                  collapsed={collapsed}
                />
              );
            }

            // Grouped item: render the NavLink parent row directly, and
            // let the Accordion animate the children open/closed. The
            // accordion is driven entirely by `activePath` — there is no
            // clickable trigger, because the parent row IS the nav link.
            return (
              <React.Fragment key={item.id}>
                <NavRow item={item} active={active} collapsed={collapsed} />
                {!collapsed && item.children.length > 0 ? (
                  <AccordionItem value={item.id} className="border-b-0">
                    <AccordionContent className="pt-0 pb-0">
                      <div className="flex flex-col">
                        {item.children.map((child) => (
                          <NavRow
                            key={child.id}
                            item={child}
                            active={isActivePath(activePath, child.path)}
                            collapsed={collapsed}
                            asSubItem
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ) : null}
              </React.Fragment>
            );
          })}
        </Accordion>
      </nav>
    </TooltipProvider>
  );
}
